import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { Link } from 'react-router-dom';
import { collection, addDoc, serverTimestamp } from 'firebase/firestore';
import { db } from '../firebase';

const AffiliateForm = () => {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    platform: '',
    audienceSize: '',
    reason: '',
  });
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [selectedTier, setSelectedTier] = useState(null);
  const [error, setError] = useState(null);

  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const handleSubmit = async (tier) => {
    if (!formData.name || !formData.email || !formData.platform) {
      setError('Please fill in all required fields.');
      return;
    }

    setSubmitting(true);
    setSelectedTier(tier);
    setError(null);

    try {
      // Check for duplicate email server-side
      const checkRes = await fetch('/.netlify/functions/check-affiliate-email', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email: formData.email }),
      });
      const checkData = await checkRes.json();
      if (checkData.exists) {
        const msg = checkData.status === 'approved'
          ? 'This email is already registered as an affiliate. Log in to your dashboard instead.'
          : 'An application with this email is already pending. Check your email for updates or log in to your dashboard.';
        setError(msg);
        setSubmitting(false);
        return;
      }

      // Save application with pending status
      await addDoc(collection(db, 'affiliate_applications'), {
        ...formData,
        tier,
        status: 'pending',
        createdAt: serverTimestamp(),
      });

      // Notify admin
      await addDoc(collection(db, 'mail'), {
        to: 'xavier@watchmindi.tv',
        message: {
          subject: `New ${tier === 'partner' ? 'Partner' : 'Standard'} Affiliate Application — ${formData.name}`,
          text: [
            `New ${tier === 'partner' ? 'Partner' : 'Standard'} Affiliate Application`,
            '',
            `Name: ${formData.name}`,
            `Email: ${formData.email}`,
            `Platform: ${formData.platform}`,
            `Audience Size: ${formData.audienceSize}`,
            `Why they want to partner: ${formData.reason || 'Not provided'}`,
            '',
            `Status: PENDING — approve this application at your admin dashboard.`,
          ].join('\n'),
        },
      });

      setSubmitted(true);
    } catch (err) {
      setError('Something went wrong. Please try again or email synemafest@gmail.com.');
    } finally {
      setSubmitting(false);
    }
  };

  if (submitted) {
    return (
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        className="bg-dark2 border border-teal/20 rounded p-10 text-center max-w-xl mx-auto"
      >
        <div className="w-16 h-16 rounded-full bg-teal/20 border border-teal/30 flex items-center justify-center mx-auto mb-6">
          <svg className="w-8 h-8 text-teal" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
          </svg>
        </div>
        <h3 className="font-display text-2xl text-white mb-3">APPLICATION SUBMITTED</h3>
        <p className="text-[0.9rem] font-light text-white/60 leading-relaxed mb-4">
          Thanks for applying for the {selectedTier === 'partner' ? 'Partner' : 'Standard'} affiliate program!
          We'll review your application and get back to you at{' '}
          <strong className="text-teal">{formData.email}</strong> within 48 hours.
        </p>
        <p className="text-[0.75rem] text-muted">
          Don't see our email? Check your spam or promotions folder for a message from synemafest@gmail.com.
        </p>
        <p className="text-[0.8rem] text-muted mt-4">
          Already approved? <Link to="/affiliate-dashboard" className="text-teal">Log in to your dashboard</Link>
        </p>
      </motion.div>
    );
  }

  return (
    <form onSubmit={(e) => e.preventDefault()} className="max-w-xl mx-auto space-y-5">
      {error && (
        <div className="bg-red-500/10 border border-red-500/20 rounded p-3 text-[0.85rem] text-red-400">
          {error}
        </div>
      )}

      <div>
        <label className="block font-mono text-[0.65rem] tracking-[0.2em] uppercase text-muted mb-2">
          Name *
        </label>
        <input
          type="text"
          name="name"
          required
          value={formData.name}
          onChange={handleChange}
          className="w-full bg-dark3 border border-white/[0.06] rounded px-4 py-3 text-[0.9rem] text-white/90 font-light focus:border-teal/40 focus:outline-none transition-colors"
          placeholder="Your full name"
        />
      </div>

      <div>
        <label className="block font-mono text-[0.65rem] tracking-[0.2em] uppercase text-muted mb-2">
          Email *
        </label>
        <input
          type="email"
          name="email"
          required
          value={formData.email}
          onChange={handleChange}
          className="w-full bg-dark3 border border-white/[0.06] rounded px-4 py-3 text-[0.9rem] text-white/90 font-light focus:border-teal/40 focus:outline-none transition-colors"
          placeholder="you@example.com"
        />
      </div>

      <div>
        <label className="block font-mono text-[0.65rem] tracking-[0.2em] uppercase text-muted mb-2">
          Platform *
        </label>
        <select
          name="platform"
          required
          value={formData.platform}
          onChange={handleChange}
          className="w-full bg-dark3 border border-white/[0.06] rounded px-4 py-3 text-[0.9rem] text-white/90 font-light focus:border-teal/40 focus:outline-none transition-colors appearance-none"
        >
          <option value="">Select your platform</option>
          <option value="YouTube">YouTube</option>
          <option value="Podcast">Podcast</option>
          <option value="Blog">Blog</option>
          <option value="Instagram">Instagram</option>
          <option value="TikTok">TikTok</option>
          <option value="Newsletter">Newsletter</option>
          <option value="Other">Other</option>
        </select>
      </div>

      <div>
        <label className="block font-mono text-[0.65rem] tracking-[0.2em] uppercase text-muted mb-2">
          Audience Size
        </label>
        <input
          type="text"
          name="audienceSize"
          value={formData.audienceSize}
          onChange={handleChange}
          className="w-full bg-dark3 border border-white/[0.06] rounded px-4 py-3 text-[0.9rem] text-white/90 font-light focus:border-teal/40 focus:outline-none transition-colors"
          placeholder="e.g., 5,000 subscribers"
        />
      </div>

      <div>
        <label className="block font-mono text-[0.65rem] tracking-[0.2em] uppercase text-muted mb-2">
          Why you want to partner
        </label>
        <textarea
          name="reason"
          rows={4}
          value={formData.reason}
          onChange={handleChange}
          className="w-full bg-dark3 border border-white/[0.06] rounded px-4 py-3 text-[0.9rem] text-white/90 font-light focus:border-teal/40 focus:outline-none transition-colors resize-none"
          placeholder="Tell us about your audience and why Synema Film Fest is a good fit..."
        />
      </div>

      <div className="flex gap-4 pt-4">
        <button
          type="button"
          disabled={submitting}
          onClick={() => handleSubmit('standard')}
          className={`flex-1 bg-teal text-dark font-body text-[0.78rem] font-semibold tracking-[0.08em] uppercase py-4 px-6 rounded-sm transition-all ${
            submitting ? 'opacity-60 cursor-wait' : 'hover:opacity-90 hover:-translate-y-[1px]'
          }`}
        >
          {submitting && selectedTier === 'standard' ? 'Submitting...' : 'Apply for Standard'}
        </button>
        <button
          type="button"
          disabled={submitting}
          onClick={() => handleSubmit('partner')}
          className={`flex-1 btn-gradient text-dark font-body text-[0.78rem] font-semibold tracking-[0.08em] uppercase py-4 px-6 rounded-sm transition-all ${
            submitting ? 'opacity-60 cursor-wait' : 'hover:opacity-90 hover:-translate-y-[1px]'
          }`}
        >
          {submitting && selectedTier === 'partner' ? 'Submitting...' : 'Apply for Partner'}
        </button>
      </div>

      <p className="text-center text-[0.8rem] text-muted pt-2">
        Already an affiliate? <Link to="/affiliate-dashboard" className="text-teal">Log in to your dashboard</Link>
      </p>
    </form>
  );
};

export default AffiliateForm;
